/**
 * FormularIndex - lookup structures over the fields of one Formular.vue instance (formularList).
 *
 * Pure functions, no Vue import: the caller passes `toRaw` (to read the structure without reactive
 * tracking) and `wrap` (Vue `reactive`, returns the cached proxy of a raw field so writes through the
 * index stay reactive). Defaults are the identity (node tests, plain objects).
 *
 * The index only describes the STRUCTURE (which field is on which page / in which group / depends on
 * which master). Field state (value, showThroughDependency, requiredMode, readonly, ...) is always read
 * live from the field objects. Structural keys used here (fieldgroupTitle, masterPropertyID,
 * masterPropertyMode, dataTypeID, nativename, classID) are not changed after Formular.reload().
 *
 * Staleness: an index is only valid for the exact formularList / page / formularFields / flatList
 * objects and flatList lengths it was built from (isFormularIndexCurrent). Any structural change
 * (new formularList, page added/removed, flatList replaced or resized) leads to a rebuild instead of
 * returning outdated field objects. Formular.vue additionally drops the cache after reload().
 */
import {StaticData} from './StaticData.js'

const identity = x => x
const EMPTY = Object.freeze([])

function pageFlatList(rawPage, toRaw) {
  const formularFields = rawPage && rawPage.formularFields ? toRaw(rawPage.formularFields) : null
  const flatList = formularFields && formularFields.formularFlatList ? toRaw(formularFields.formularFlatList) : null
  return {formularFields, flatList}
}

function pushToMap(map, key, value) {
  const list = map.get(key)
  if (list) list.push(value)
  else map.set(key, [value])
}

/**
 * Build the index for a formularList (array of formular pages with formularFields.formularFlatList).
 *
 * @returns {{
 *   source: Array, pages: Array, pageByFormular: Map, allFields: Array,
 *   byNativename: Map, byClassID: Map, dependentsByMaster: Map, subclassFields: Array
 * }}
 *  - pages[i]: {formular, formularFields, flatList, length, fields, groups, byNativename, byClassID}
 *      groups: Map fieldgroupTitle -> {fields: [field], positions: [index in flatList]} - groups in
 *      order of their first field, fields in flatList order. Keys compare like `===` (undefined and ""
 *      are different groups, exactly like the template's strict comparison).
 *  - allFields: every field of all pages in formular order (page by page, flatList order)
 *  - byNativename / byClassID: key -> [field] in formular order (first entry = first match of a scan).
 *    Within one Formular.vue instance all fields share the same subtypeCount, so no extra key is needed.
 *  - dependentsByMaster: masterPropertyID -> [{field, order}] of all fields with
 *    masterPropertyMode !== NONE, in formular order (order = position in allFields)
 *  - subclassFields: [{field, order}] of all TYPE_SUBCLASS fields in formular order
 */
export function buildFormularIndex(formularList, {toRaw = identity, wrap = identity} = {}) {
  const source = formularList ? toRaw(formularList) : null
  const index = {
    source,
    pages: [],
    pageByFormular: new Map(),
    allFields: [],
    byNativename: new Map(),
    byClassID: new Map(),
    dependentsByMaster: new Map(),
    subclassFields: []
  }
  if (!source) return index

  const NONE = StaticData.FORM_MASTERPROPERTY_MODES.NONE
  const SUBCLASS = StaticData.DATATYPES.TYPE_SUBCLASS

  for (let p = 0; p < source.length; p++) {
    const formular = source[p] ? toRaw(source[p]) : source[p]
    const {formularFields, flatList} = pageFlatList(formular, toRaw)
    const page = {
      formular, formularFields, flatList, length: flatList ? flatList.length : -1,
      fields: [], groups: new Map(), byNativename: new Map(), byClassID: new Map()
    }
    if (flatList) {
      for (let j = 0; j < flatList.length; j++) {
        const raw = flatList[j] ? toRaw(flatList[j]) : flatList[j]
        if (!raw) {
          page.fields.push(raw)
          continue
        }
        const field = wrap(raw)
        const order = index.allFields.length
        page.fields.push(field)
        index.allFields.push(field)

        let group = page.groups.get(raw.fieldgroupTitle)
        if (!group) {
          group = {fields: [], positions: []}
          page.groups.set(raw.fieldgroupTitle, group)
        }
        group.fields.push(field)
        group.positions.push(j)

        pushToMap(page.byNativename, raw.nativename, field)
        pushToMap(page.byClassID, raw.classID, field)
        pushToMap(index.byNativename, raw.nativename, field)
        pushToMap(index.byClassID, raw.classID, field)

        if (raw.masterPropertyMode !== NONE) pushToMap(index.dependentsByMaster, raw.masterPropertyID, {field, order})
        if (raw.dataTypeID === SUBCLASS) index.subclassFields.push({field, order})
      }
    }
    index.pages.push(page)
    if (formular && typeof formular === 'object' && !index.pageByFormular.has(formular)) index.pageByFormular.set(formular, p)
  }
  return index
}

/** True if `index` was built from exactly this formularList structure (identities and lengths). */
export function isFormularIndexCurrent(index, formularList, {toRaw = identity} = {}) {
  if (!index) return false
  const source = formularList ? toRaw(formularList) : null
  if (index.source !== source) return false
  if (!source) return true
  if (source.length !== index.pages.length) return false
  for (let p = 0; p < source.length; p++) {
    const page = index.pages[p]
    const formular = source[p] ? toRaw(source[p]) : source[p]
    if (page.formular !== formular) return false
    const {formularFields, flatList} = pageFlatList(formular, toRaw)
    if (page.formularFields !== formularFields || page.flatList !== flatList) return false
    if ((flatList ? flatList.length : -1) !== page.length) return false
  }
  return true
}

/** Return `cached` when it is still current for formularList, otherwise a freshly built index. */
export function currentFormularIndex(cached, formularList, options = {}) {
  return isFormularIndexCurrent(cached, formularList, options) ? cached : buildFormularIndex(formularList, options)
}

/** Page entry of a formular object (raw or proxy) of this index, or null if it is not part of it. */
export function indexPageOf(index, formular, {toRaw = identity} = {}) {
  if (!index || !formular || typeof formular !== 'object') return null
  const p = index.pageByFormular.get(toRaw(formular))
  return p === undefined ? null : index.pages[p]
}

/** Fields of one group of a page in flatList order (empty array if the group does not exist). */
export function groupFields(page, fieldgroupTitle) {
  const group = page ? page.groups.get(fieldgroupTitle) : null
  return group ? group.fields : EMPTY
}

/** Dependents ({field, order}) whose masterPropertyID === masterId, in formular order. */
export function dependentsOf(index, masterId) {
  return (index && index.dependentsByMaster.get(masterId)) || EMPTY
}

/**
 * Per group the first field (in flatList order) accepted by `accept(field, title)`, ordered by the
 * position of that field - the same result as the scan "first accepted field of each not yet seen
 * group" over the flatList. `accept` is called with live (reactive) fields.
 */
export function firstFieldPerGroup(page, accept) {
  const heads = []
  if (!page) return heads
  for (const [title, group] of page.groups) {
    const fields = group.fields
    for (let i = 0; i < fields.length; i++) {
      if (accept(fields[i], title)) {
        heads.push({position: group.positions[i], field: fields[i]})
        break
      }
    }
  }
  heads.sort((a, b) => a.position - b.position)
  return heads.map(h => h.field)
}
